// Group Types - Single source of truth for all group definitions
export const GROUP_TYPES = {
  ADMIN: {
    name: 'Administrators',
    type: 'ADMIN',
    description: 'System administrators with full access',
    isDefault: true,
    permissions: {
      vault: {
        read: true,
        write: true,
        upload: true,
        delete: true,
        grant: true,
        purge: true,
        restore: true
      },
      portal: {
        view: true,
        write: true,
        change: true,
        delete: true,
        customize: true,
        manageUsers: true,
        manageGroups: true
      },
      features: {
        ANNOUNCEMENTS: ['view', 'write', 'change', 'delete', 'manage'],
        EVENTS: ['view', 'write', 'change', 'delete', 'customize', 'manage'],
        MESSAGING: ['view', 'write', 'delete', 'manage'],
        PHOTOS: ['view', 'upload', 'share', 'delete', 'manage'],
        DOCUMENTS: ['view', 'upload', 'share', 'delete', 'manage'],
        FAMILY_TREE: ['view', 'edit_own', 'edit_children', 'manage'],
        ADMIN_PORTAL: ['access', 'manage']
      }
    }
  },
  IMMEDIATE_FAMILY: {
    name: 'Immediate Family',
    type: 'IMMEDIATE_FAMILY',
    description: 'Core family members with full access',
    isDefault: true,
    permissions: {
      vault: {
        read: true,
        write: true,
        upload: true,
        delete: true,
        grant: true
      },
      portal: {
        view: true,
        write: true,
        change: true,
        delete: true,
        customize: true
      },
      features: {
        ANNOUNCEMENTS: ['view', 'write', 'change', 'delete'],
        EVENTS: ['view', 'write', 'change', 'delete', 'customize'],
        MESSAGING: ['view', 'write', 'delete'],
        PHOTOS: ['view', 'upload', 'share', 'delete'],
        DOCUMENTS: ['view', 'upload', 'share', 'delete'],
        FAMILY_TREE: ['view', 'edit_own', 'edit_children']
      }
    }
  },
  PARENTS: {
    name: 'Parents',
    type: 'PARENTS',
    description: 'Parent members with elevated access and child management',
    isDefault: true,
    permissions: {
      vault: {
        read: true,
        write: true,
        upload: true,
        delete: true,
        grant: true
      },
      portal: {
        view: true,
        write: true,
        change: true,
        delete: true,
        customize: true
      },
      features: {
        ANNOUNCEMENTS: ['view', 'write', 'change', 'delete'],
        EVENTS: ['view', 'write', 'change', 'delete', 'customize'],
        MESSAGING: ['view', 'write', 'delete'],
        PHOTOS: ['view', 'upload', 'share', 'delete'],
        DOCUMENTS: ['view', 'upload', 'share', 'delete'],
        FAMILY_TREE: ['view', 'edit_own', 'edit_children'],
        CHILDREN: ['view', 'manage']
      }
    }
  },
  ELDERS: {
    name: 'Elders',
    type: 'ELDERS',
    description: 'Senior family members with elevated access',
    isDefault: true,
    permissions: {
      vault: {
        read: true,
        write: true,
        upload: true,
        delete: false,
        grant: false
      },
      portal: {
        view: true,
        write: true,
        change: false,
        delete: false,
        customize: false
      },
      features: {
        ANNOUNCEMENTS: ['view', 'write'],
        EVENTS: ['view', 'write'],
        MESSAGING: ['view', 'write'],
        PHOTOS: ['view', 'upload', 'share'],
        DOCUMENTS: ['view', 'upload', 'share'],
        FAMILY_TREE: ['view', 'edit_own']
      }
    }
  },
  EXTENDED_FAMILY: {
    name: 'Extended Family',
    type: 'EXTENDED_FAMILY',
    description: 'Extended family members with limited access',
    isDefault: true,
    permissions: {
      vault: {
        read: true,
        write: true,
        upload: true,
        delete: false,
        grant: false
      },
      portal: {
        view: true,
        write: true,
        change: 'own',
        delete: 'own',
        customize: false
      },
      features: {
        ANNOUNCEMENTS: ['view', 'write'],
        EVENTS: ['view', 'write'],
        MESSAGING: ['view', 'write'],
        PHOTOS: ['view', 'upload'],
        DOCUMENTS: ['view', 'upload'],
        FAMILY_TREE: ['view']
      }
    }
  },
  PARENTS_SIBLINGS: {
    name: 'Parents Siblings',
    type: 'PARENTS_SIBLINGS',
    description: 'Siblings of Parents',
    isDefault: true,
    permissions: {
      vault: {
        read: true,
        write: true,
        upload: true,
        delete: false,
        grant: false
      },
      portal: {
        view: true,
        write: true,
        change: 'own',
        delete: 'own',
        customize: false
      },
      features: {
        ANNOUNCEMENTS: ['view', 'write'],
        EVENTS: ['view', 'write'],
        MESSAGING: ['view', 'write'],
        PHOTOS: ['view', 'upload'],
        DOCUMENTS: ['view', 'upload'],
        FAMILY_TREE: ['view']
      }
    }
  },
  SIBLING_CHILDREN: {
    name: 'Sibling Children',
    type: 'SIBLING_CHILDREN',
    description: 'Children of Siblings',
    isDefault: true,
    permissions: {
      vault: {
        read: true,
        write: true,
        upload: true,
        delete: false,
        grant: false
      },
      portal: {
        view: true,
        write: true,
        change: 'own',
        delete: 'own',
        customize: false
      },
      features: {
        ANNOUNCEMENTS: ['view', 'write'],
        EVENTS: ['view', 'write'],
        MESSAGING: ['view', 'write'],
        PHOTOS: ['view', 'upload'],
        DOCUMENTS: ['view', 'upload'],
        FAMILY_TREE: ['view']
      }
    }
  },
  DISTANT_FAMILY: {
    name: 'Distant Family',
    type: 'DISTANT_FAMILY',
    description: 'Extended family members with limited access',
    isDefault: true,
    permissions: {
      vault: {
        read: true,
        write: false,
        upload: false,
        delete: false,
        grant: false
      },
      portal: {
        view: true,
        write: false,
        change: false,
        delete: false,
        customize: false
      },
      features: {
        ANNOUNCEMENTS: ['view'],
        EVENTS: ['view'],
        MESSAGING: ['view'],
        PHOTOS: ['view'],
        DOCUMENTS: ['view'],
        FAMILY_TREE: ['view']
      }
    }
  },

  // ── Groups matching PROD ──────────────────────────────────────────────────

  GRANDPARENTS: {
    name: 'Grandparents',
    type: 'GRANDPARENTS',
    description: 'Grandparent family members with elevated viewing access',
    isDefault: true,
    permissions: {
      vault: {
        read: true,
        write: true,
        upload: true,
        delete: false,
        grant: false
      },
      portal: {
        view: true,
        write: true,
        change: false,
        delete: false,
        customize: false
      },
      features: {
        ANNOUNCEMENTS: ['view', 'write'],
        EVENTS: ['view', 'write'],
        MESSAGING: ['view', 'write'],
        PHOTOS: ['view', 'upload', 'share'],
        DOCUMENTS: ['view', 'upload', 'share'],
        FAMILY_TREE: ['view', 'edit_own']
      }
    }
  },

  FRIENDS_OF_FAMILY: {
    name: 'Friends of Family',
    type: 'FRIENDS_OF_FAMILY',
    description: 'Close friends with limited family access',
    isDefault: true,
    permissions: {
      vault: {
        read: false,
        write: false,
        upload: false,
        delete: false,
        grant: false
      },
      portal: {
        view: true,
        write: false,
        change: false,
        delete: false,
        customize: false
      },
      features: {
        ANNOUNCEMENTS: ['view'],
        EVENTS: ['view'],
        MESSAGING: ['view', 'write'],
        PHOTOS: ['view'],
        DOCUMENTS: ['view'],
        FAMILY_TREE: ['view']
      }
    }
  },

  CAREGIVERS: {
    name: 'Caregivers',
    type: 'CAREGIVERS',
    description: 'Professional or personal caregivers with health-related access',
    isDefault: true,
    isExternal: false,
    permissions: {
      vault: {
        read: false,
        write: false,
        upload: false,
        delete: false,
        grant: false
      },
      portal: {
        view: true,
        write: false,
        change: false,
        delete: false,
        customize: false
      },
      features: {
        MESSAGING: ['view', 'write'],
        HEALTH: ['view'],
        EMERGENCY_INFO: ['view']
      }
    }
  },

  CHATGROUP: {
    name: 'Chat Group',
    type: 'CHATGROUP',
    description: 'Members with messaging access only',
    isDefault: true,
    permissions: {
      vault: {
        read: false,
        write: false,
        upload: false,
        delete: false,
        grant: false
      },
      portal: {
        view: true,
        write: false,
        change: false,
        delete: false,
        customize: false
      },
      features: {
        MESSAGING: ['view', 'write'],
        DOCUMENTS: ['view']
      }
    }
  },

  EMERGENCY_CONTACTS: {
    name: 'Emergency Contacts',
    type: 'EMERGENCY_CONTACTS',
    description: 'Emergency contacts — messaging only, no family data access',
    isDefault: true,
    isExternal: true,
    permissions: {
      vault: {
        read: false,
        write: false,
        upload: false,
        delete: false,
        grant: false
      },
      portal: {
        view: false,
        write: false,
        change: false,
        delete: false,
        customize: false
      },
      features: {
        MESSAGING: ['view', 'write']
      }
    }
  },

  CHILDREN_MINORS: {
    name: 'Children & Minors',
    type: 'CHILDREN_MINORS',
    description: 'Under-18 family members — age-appropriate read access and limited participation',
    isDefault: true,
    permissions: {
      vault: {
        read: false,
        write: false,
        upload: false,
        delete: false,
        grant: false
      },
      portal: {
        view: true,
        write: false,
        change: false,
        delete: false,
        customize: false
      },
      features: {
        ANNOUNCEMENTS: ['view'],
        EVENTS: ['view', 'write'],
        MESSAGING: ['view', 'write'],
        PHOTOS: ['view', 'upload'],
        DOCUMENTS: ['view'],
        FAMILY_TREE: ['view']
      }
    }
  },

  // ── Professional / External Groups ───────────────────────────────────────
  // isExternal: true — these members ONLY see explicitly shared files + DMs.
  // Family-wide collections (events, photos, health, announcements) are blocked
  // at the Firestore rules level by the isExternalMember() check.

  ADVISORS: {
    name: 'Professional Advisors',
    type: 'ADVISORS',
    description: 'Professional advisors (general). Messaging + explicitly shared documents only.',
    isDefault: true,
    isExternal: true,
    permissions: {
      vault: {
        read: false,
        write: false,
        upload: false,
        delete: false,
        grant: false
      },
      portal: {
        view: false,
        write: false,
        change: false,
        delete: false,
        customize: false
      },
      features: {
        MESSAGING: ['view', 'write'],
        DOCUMENTS: ['view']
      }
    }
  },

  LEGAL_FINANCIAL: {
    name: 'Legal & Financial',
    type: 'LEGAL_FINANCIAL',
    description: 'Attorneys, accountants, financial planners. Messaging + explicitly shared documents only.',
    isDefault: true,
    isExternal: true,
    permissions: {
      vault: {
        read: false,
        write: false,
        upload: false,
        delete: false,
        grant: false
      },
      portal: {
        view: false,
        write: false,
        change: false,
        delete: false,
        customize: false
      },
      features: {
        MESSAGING: ['view', 'write'],
        DOCUMENTS: ['view']
      }
    }
  }
};

// Helper functions for permission management
export const getGroupPermissions = (groupType) => {
  return GROUP_TYPES[groupType]?.permissions || GROUP_TYPES.EXTENDED_FAMILY.permissions;
};

export const hasPermission = (groupType, category, permission) => {
  const permissions = getGroupPermissions(groupType);
  if (!permissions || !permissions[category]) return false;
  
  const categoryPerms = permissions[category];
  if (Array.isArray(categoryPerms)) {
    return categoryPerms.includes(permission);
  }
  return !!categoryPerms[permission];
};

export const formatPermissions = (permissions) => {
  if (!permissions) return 'None';
  
  const formatSection = (section) => {
    if (!section) return 'None';
    if (typeof section === 'string') return section;
    if (section.name) return section.name;
    
    return Object.entries(section)
      .filter(([_, value]) => value === true)
      .map(([key]) => key.charAt(0).toUpperCase() + key.slice(1))
      .join(', ') || 'None';
  };

  const sections = [];
  if (permissions.vault) sections.push(`Vault: ${formatSection(permissions.vault)}`);
  if (permissions.portal) sections.push(`Portal: ${formatSection(permissions.portal)}`);
  if (permissions.features) {
    const features = Object.entries(permissions.features)
      .map(([feature, perms]) => `${feature}: ${Array.isArray(perms) ? perms.join(', ') : perms}`)
      .join('\n');
    sections.push(`Features:\n${features}`);
  }

  return sections.join('\n');
};

// Vault permission levels
export const VAULT_PERMISSIONS = {
  READ_ONLY: {
    name: 'Read Only',
    permissions: {
      read: true,
      write: false,
      upload: false,
      delete: false,
      grant: false
    }
  },
  READ_WRITE: {
    name: 'Read & Write',
    permissions: {
      read: true,
      write: true,
      upload: true,
      delete: false,
      grant: false
    }
  },
  FULL_ACCESS: {
    name: 'Full Access',
    permissions: {
      read: true,
      write: true,
      upload: true,
      delete: true,
      grant: true
    }
  }
};

// Portal permission levels
export const PORTAL_PERMISSIONS = {
  VIEWER: {
    name: 'Viewer',
    permissions: {
      view: true,
      write: false,
      change: false,
      delete: false,
      customize: false
    }
  },
  CONTRIBUTOR: {
    name: 'Contributor',
    permissions: {
      view: true,
      write: true,
      change: 'own',
      delete: 'own',
      customize: false
    }
  },
  ADMIN: {
    name: 'Administrator',
    permissions: {
      view: true,
      write: true,
      change: true,
      delete: true,
      customize: true
    }
  }
};

// Feature permissions mapping
export const FEATURE_PERMISSIONS = {
  ANNOUNCEMENTS: ['view', 'write', 'change', 'delete', 'manage'],
  EVENTS: ['view', 'write', 'change', 'delete', 'customize', 'manage'],
  MESSAGING: ['view', 'write', 'delete', 'manage'],
  PHOTOS: ['view', 'upload', 'share', 'delete', 'manage'],
  DOCUMENTS: ['view', 'upload', 'share', 'delete', 'manage'],
  FAMILY_TREE: ['view', 'edit_own', 'edit_children', 'manage'],
  CHILDREN: ['view', 'manage'],
  ADMIN_PORTAL: ['access', 'manage']
};

// Generate defaultGroups from GROUP_TYPES
export const defaultGroups = Object.entries(GROUP_TYPES).reduce((acc, [key, value]) => {
  acc[value.name] = {
    isDefault: true,
    name: value.name,
    description: value.description,
    permissions: value.permissions,
    members: [],
    createdAt: null,
    updatedAt: null,
    version: '1.0'
  };
  return acc;
}, {});

// Group Schema Validation
export const groupSchema = {
  isDefault: 'boolean',
  name: 'string',
  description: 'string',
  permissions: {
    vault: 'object',
    portal: 'object',
    features: 'object?'
  },
  members: 'string[]',
  createdAt: 'timestamp',
  updatedAt: 'timestamp',
  version: 'string'
};

// Default Group Keys
export const DEFAULT_GROUP_KEYS = Object.keys(GROUP_TYPES);

// Group Version
export const GROUP_VERSION = '1.0';

// External (professional) group types — restricted to messaging + shared docs only.
// Firestore rules use isExternalMember() which checks this list via the user's
// isExternal flag (set by joinFamily when all assigned groups are external).
export const EXTERNAL_GROUP_TYPES = Object.entries(GROUP_TYPES)
  .filter(([, def]) => def.isExternal === true)
  .map(([key]) => key);

/**
 * Compute merged feature permissions for a set of group types.
 * Returns a flat map: { FEATURE: { permission: true, ... }, ... }
 * Stored on the user doc so Firestore rules can read without cross-collection lookups.
 */
export const computeFeaturePermissions = (groupTypes = []) => {
  const merged = {};
  for (const groupType of groupTypes) {
    const groupDef = GROUP_TYPES[groupType];
    if (!groupDef) continue;
    for (const [feature, perms] of Object.entries(groupDef.permissions.features || {})) {
      if (!merged[feature]) merged[feature] = {};
      for (const perm of perms) {
        merged[feature][perm] = true;
      }
    }
  }
  return merged;
};

/**
 * Returns true if every group in the list is an external (professional) group.
 * Used to set the isExternal flag on the user document.
 */
export const isExternalGroupSet = (groupTypes = []) =>
  groupTypes.length > 0 && groupTypes.every(g => EXTERNAL_GROUP_TYPES.includes(g));